import { useState, useRef, useMemo } from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuList from '@mui/material/MenuList'
import Divider from '@mui/material/Divider'
import { camelize } from 'humps'
import { useTranslation } from 'react-i18next'

import { getDefaultGroup } from 'components/Participants/Group/GetDefaultGroup'
import LoadGroups from 'components/Participants/Group/GroupDropdown/LoadGroups'
import { hasCustomGroup } from 'components/Participants/Group/Utils'
import { DeleteGroupModal } from 'components/Participants/Group'
import type { Group as GroupType } from 'containers/Participants/Redux/Reducer/InitialStateType'
import OptionWithTooltip, {
  shouldShowTooltip,
} from 'components/Shared/OptionWithTooltip'
import { useAppSelector } from 'redux/Hook'

const FilterGroupListButton = () => {
  const [open, setOpen] = useState(false)
  const [isDeleteGroupModalOpen, setDeleteGroupModalVisibility] =
    useState(false)
  const [deleteGroup, setDeleteGroup] = useState<GroupType>({
    id: '',
    name: '',
    defaultSegment: false,
  })

  const { t } = useTranslation()
  const defaultGroup = getDefaultGroup()

  const { data: groups, activeGroup } = useAppSelector(
    (state) => state.participantGroups,
  )

  const { adminLocale } = useAppSelector((state) => state.user.user)

  const anchorRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }

    setOpen(false)
  }

  const getActiveGroupName = (): JSX.Element | string => {
    let groupName = activeGroup?.name || defaultGroup.name

    if (activeGroup && activeGroup.defaultSegment) {
      // only default group can be translated
      const group =
        groups.find((group) => group.id === activeGroup.id) || defaultGroup
      groupName = t(`filters.participantGroup.${camelize(group.name)}`)
    }

    if (shouldShowTooltip(groupName, 48))
      return <OptionWithTooltip option={groupName} charLimit={45} />
    else return groupName
  }

  const getDefaultGroups = () => {
    const defaultGroups: GroupType[] = groups.filter(
      (group) => group.defaultSegment,
    )
    const defaultGroupsWithTranslatedNames = defaultGroups.map((group) => ({
      ...group,
      name: t(`filters.participantGroup.${camelize(group.name)}`),
    }))

    return defaultGroupsWithTranslatedNames
  }

  const getCustomGroups = () => {
    return groups.filter((group: GroupType) => !group.defaultSegment)
  }

  const showDivider = useMemo(() => hasCustomGroup(groups), [groups])
  const customGroups = useMemo(() => getCustomGroups(), [groups])
  const defaultGroups = useMemo(() => getDefaultGroups(), [groups, adminLocale])

  return (
    <>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        data-testid="filter-group-list-current"
        size="large"
      >
        <Button sx={{ textTransform: 'none' }}>{getActiveGroupName()}</Button>
        <Button
          size="large"
          onClick={handleToggle}
          data-testid="filter-group-lists"
          id="groupDropDown"
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        placement="bottom-start"
        anchorEl={anchorRef.current}
        transition
        sx={{
          zIndex: 2,
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              sx={{
                maxHeight: '300px',
                overflowY: 'scroll',
                width: 370,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  <LoadGroups
                    handleToggle={handleToggle}
                    setDeleteGroup={setDeleteGroup}
                    groups={[defaultGroup, ...defaultGroups]}
                    setDeleteGroupModalVisibility={
                      setDeleteGroupModalVisibility
                    }
                  />
                  {showDivider && <Divider />}
                  <LoadGroups
                    handleToggle={handleToggle}
                    setDeleteGroup={setDeleteGroup}
                    groups={customGroups}
                    setDeleteGroupModalVisibility={
                      setDeleteGroupModalVisibility
                    }
                  />
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {isDeleteGroupModalOpen && (
        <DeleteGroupModal
          deleteGroup={deleteGroup}
          isOpen={isDeleteGroupModalOpen}
          setModalVisibility={setDeleteGroupModalVisibility}
        />
      )}
    </>
  )
}

export default FilterGroupListButton
